import type { CSSProperties } from "react";

/**
 * ── DER AUFTRITT DER HUELLE ─────────────────────────────────────────────────
 *
 * `ui/Aufbau.tsx` staffelt den INHALT einer Seite — Kopf, Bedienspalte, Zeilen.
 * Die Huelle drumherum (Kopfzeile und Seitenleiste) lief bis 0.281.3 daran
 * vorbei. Nachgemessen in ai-chat am 2026-08-22:
 *
 *   76 ms   App-Zeichen, Wortmarke UND alle Eintraege der Seitenleiste
 *           springen gemeinsam auf volle Deckkraft — kein Auftritt, kein Fade
 *   701 ms  Theme-Knopf erscheint, 625 ms nach dem Rest. Der einzige Versatz
 *           im ganzen Bild, und er ist unbeabsichtigt: er haengt daran, wann
 *           sein `mounted`-Gate faellt
 *   1550 ms Die Liste erscheint mit 299 Eintraegen auf einen Schlag
 *
 * Die Huelle bekommt hier KEINE zweite Mechanik. Klasse, Kurve und Dauer sind
 * dieselben wie beim Inhalt (`AUFBAU_KLASSE`, `globals.css`); nur der Takt ist
 * ein eigener, weil die Huelle aus kleinen Zeichen dicht nebeneinander besteht
 * und nicht aus Abschnitten.
 *
 * ── DIE KOPFZEILE LAEUFT VON AUSSEN NACH INNEN ──────────────────────────────
 *
 * Die beiden aeusseren Anker beginnen GEMEINSAM: das App-Zeichen ganz links und
 * der Konto-Knopf ganz rechts. Danach fuellt sich die Zeile nach innen — links
 * nach rechts, rechts nach links.
 *
 *     [ Zeichen ]  Wortmarke  ····················  Knoepfe  [ Konto ]
 *         0 ms       60 ms                           60 ms     0 ms
 *          →           →                               ←         ←
 *
 * | Stufe            | t      | Was                                          |
 * |------------------|--------|----------------------------------------------|
 * | `HUELLE_MARKE`   |   0 ms | App-Zeichen — linker Anker                   |
 * | `HUELLE_KONTO`   |   0 ms | Konto-Knopf — rechter Anker, GLEICHZEITIG    |
 * |                  |  60 ms | Wortmarke — legt sich ab hier Zeichen fuer   |
 * |                  |        | Zeichen frei (das IST ihr Auftritt)          |
 * | `HUELLE_KNOEPFE` |  60 ms | der Knopf INNEN NEBEN dem Konto, dann je 60  |
 * |                  |        | ms weiter nach links                         |
 * | `HUELLE_LEISTE`  | 120 ms | erster Eintrag der Seitenleiste, dann je 60  |
 * | `HUELLE_LISTE`   | 300 ms | eine Liste, die zum Seitenanfang schon steht |
 *
 * DREI ENTSCHEIDUNGEN, DIE MAN DEN ZAHLEN NICHT ANSIEHT:
 *
 * 1. **`HUELLE_KONTO` ist 0 und damit gleich `HUELLE_MARKE` — Absicht, kein
 *    Versehen.** Es bleiben zwei Konstanten, weil es zwei Anker an zwei
 *    Raendern sind; wer einen verschiebt, soll den anderen nicht mitnehmen.
 *
 *    Bis 0.291.0 lief die rechte Seite in DOM-Reihenfolge und das Konto stand
 *    fest auf 300 ms. Beides zusammen ergab in jeder App mit nur einem
 *    Steuerelement ein Loch: Theme-Knopf bei 120 ms, dann 180 ms nichts, dann
 *    das Konto. Zwei Schlaege statt eines Laufs — und die feste 300 machte es
 *    unabhaengig davon, wie viele Knoepfe wirklich davor standen.
 *
 * 2. **Kopfzeile und Seitenleiste beginnen NICHT gleichzeitig, aber sie
 *    ueberlappen** (60 zu 120 ms). Sie stehen an verschiedenen Raendern des
 *    Bildes; strikt nacheinander gelesen wartet die eine auf die andere, ohne
 *    dass jemand den Zusammenhang sieht.
 *
 * 3. **`HUELLE_LISTE` gilt nur fuer eine Liste, die beim Seitenanfang schon
 *    dasteht.** Wer erst aus einer Server Action kommt, laesst ihn WEG und
 *    nimmt nur den Versatz aus `aufbauStil` — eine CSS-Animation beginnt beim
 *    Mounten, die Stufenzeit wuerde also obendrauf gewartet. In ai-chat
 *    erschien die erste Zeile dadurch bei 1643 statt 1415 ms, und die halbe
 *    Sekunde dazwischen stand die Liste im DOM und war unsichtbar.
 *
 * Alles laeuft ueber `--jw-ab`, das `globals.css` mit `--jw-versatz` addiert.
 * Wer Bewegung abbestellt hat, sieht alles sofort (die Regel dort steht in
 * `@media (prefers-reduced-motion: no-preference)`).
 */

export const HUELLE_MARKE = 0;
/**
 * Der erste Knopf der rechten Gruppe — und das ist der, der INNEN NEBEN dem
 * Konto steht, nicht der linkeste. Die rechte Seite laeuft nach innen, also in
 * umgekehrter DOM-Reihenfolge; `TopNavActions` dreht den Index dafuer um.
 */
export const HUELLE_KNOEPFE = 60;
export const HUELLE_LEISTE = 120;
export const HUELLE_LISTE = 300;

/**
 * Der TITEL DER KOPFZEILE DES INHALTS (`PageHeader variant="toolbar"`).
 *
 * Er legt sich frei wie der App-Name, mit derselben Mechanik (`tippStil`).
 *
 * ── WARUM 0 UND NICHT 120 ───────────────────────────────────────────────────
 *
 * Er stand bis 0.311.0 auf 120 ms — derselbe Zeitpunkt wie der erste Eintrag der
 * Seitenleiste, mit der Begruendung, Leiste und Kopfzeile seien zusammen der
 * Rahmen um den Inhalt. Beim OEFFNEN der App stimmt das. Beim SEITENWECHSEL
 * nicht: dann bewegt sich die Huelle gar nicht (sie bleibt stehen, das ist ihr
 * Sinn), und es laufen nur noch Kopfzeile und Inhalt. Der Inhalt beginnt bei 0
 * (`Stufe index={0}`) — die Kopfzeile kam also NACH dem, was sie ueberschreibt.
 * Im Bild gemessen (2026-08-22, Wechsel Uebersicht → Belege): die Startflaeche
 * stand schon halb da, waehrend die Kopfzeile noch leer war.
 *
 * Bei 0 fuehrt sie: ihre Schrift ist klein und nach 300 ms fertig, waehrend der
 * Kopf darunter noch 460 ms fliegt. Man liest Kopfzeile, dann Inhalt — in beiden
 * Faellen.
 */
export const HUELLE_KOPFZEILE = 0;

/**
 * Die Beschreibung unter dem Titel — und die Bedienelemente LINKS neben dem
 * Titel (`inline`), die von innen nach aussen zaehlen.
 *
 * Sie kommt NACH dem Titel (60 statt 0) und legt sich seit 0.335.0 Zeichen fuer
 * Zeichen frei wie er, statt nur einzublenden: zwei Textzeilen uebereinander,
 * von denen die eine tippt und die andere fadet, lesen sich als zwei
 * verschiedene Bauteile (Betreiber am 2026-08-23: „Beschreibungstext soll wie
 * Titel schreiben").
 *
 * Die Knoepfe RECHTS zaehlen dagegen von `HUELLE_KOPF_ANKER` aus nach innen —
 * dieselbe Form wie in der App-Kopfzeile darueber (`TopNavActions`), damit die
 * beiden Zeilen nicht gegeneinander laufen. Der Abstand ist `HUELLE_SCHRITT`;
 * enger gesetzt liest sich die Zeile als EIN Schlag statt als Lauf.
 *
 * WAS SICH DABEI NIE BEWEGT: der Rahmen. Kante, Grund und `sticky` der Zeile
 * tragen KEINE Auftrittsklasse — beim Seitenwechsel wird der Kopf neu gemountet,
 * und eine Kante, die sich bei jedem Klick neu zeichnet, liest sich als Ruckeln
 * der ganzen Anwendung. Sie steht; nur ihr Inhalt laeuft ein.
 */
export const HUELLE_KOPF_BEIWERK = 60;

/**
 * Der Konto-Knopf — der RECHTE ANKER, gleichzeitig mit dem App-Zeichen.
 *
 * Eigener Wert und nicht einfach `HUELLE_MARKE`: es sind zwei Anker an zwei
 * Raendern. Dass sie denselben Zeitpunkt teilen, ist die Aussage — wer einen
 * verschiebt, soll den anderen nicht stillschweigend mitziehen.
 *
 * Er ist bewusst KEINE gezaehlte Position mehr (`HUELLE_KNOEPFE + n *
 * HUELLE_SCHRITT`): wie viele Steuerelemente neben ihm stehen, entscheidet jede
 * App selbst (ai-chat reicht eines hinein, mail drei). Als Anker braucht er die
 * Zahl nicht zu kennen — die Knoepfe zaehlen von IHM aus nach innen.
 */
export const HUELLE_KONTO = 0;

/**
 * Der AEUSSERSTE Knopf der Inhalts-Kopfzeile — ihr rechter Anker.
 *
 * Bis 0.335.0 gab es ihn nicht: die Inhaltszeile hatte nur den linken Anker
 * (den Titel bei `HUELLE_KOPFZEILE` = 0), rechts begann ALLES erst bei 60 ms.
 * Die App-Kopfzeile darueber hat dagegen ZWEI Anker auf 0 (`HUELLE_MARKE`
 * links, `HUELLE_KONTO` rechts) und fuellt sich von beiden Raendern nach innen.
 * Die beiden Zeilen liefen damit nach verschiedenen Regeln — der Betreiber am
 * 2026-08-23: „die rechten Elemente von rechts nach links wie im App-Header".
 *
 * Beide Zeilen haben jetzt dieselbe Form: aussen die Anker, dann nach innen je
 * `HUELLE_SCHRITT`. Eigener Name statt `HUELLE_KONTO`, aus demselben Grund, aus
 * dem der Konto-Knopf nicht `HUELLE_MARKE` heisst — es sind verschiedene Anker
 * in verschiedenen Zeilen; wer einen verschiebt, soll die anderen nicht
 * stillschweigend mitziehen.
 */
export const HUELLE_KOPF_ANKER = 0;

/**
 * Abstand zwischen zwei Zeichen der Huelle.
 *
 * 40 ms waren zu eng — nicht fuer sich, sondern IM VERHAELTNIS ZUR DAUER. Bei
 * den 340 ms von `.jw-aufbau` waren dadurch achteinhalb Elemente gleichzeitig
 * unterwegs, und gemessen am 2026-08-22 erschienen vier davon innerhalb von
 * 13 ms (Marke, erster Knopf, zwei Eintraege der Seitenleiste), gefolgt von
 * einer Luecke von 117 ms und dann dem Rest. Das liest sich als Wolke mit
 * zwei Schlaegen, nicht als Folge.
 *
 * Massgeblich ist das Verhaeltnis: bei Versatz ≥ ~⅓ der Dauer sieht man
 * einzelne Schlaege. 60 zu 200 ist 0,30 — gut drei Elemente gleichzeitig statt
 * achteinhalb.
 *
 * DIE ZWEITE GROESSE IST DIE GESAMTDAUER, und sie zaehlt mehr als die Eleganz
 * der Staffelung: der erste Paint liegt auf Labs je nach Seite bei 368 bis
 * 1380 ms, und alles hier kommt oben DRAUF (gemessen 2026-08-22, siehe
 * `Aufbau.tsx`, das aus demselben Grund von 110 auf 70 ms gestrafft wurde).
 * Eine Choreografie, die fuer sich genommen schoen ist, macht eine Seite zaeh,
 * wenn sie sich auf eine ohnehin lange Ladezeit addiert. Der letzte Schlag der
 * Huelle steht deshalb 500 ms nach dem ersten Bild, nicht 570.
 */
export const HUELLE_SCHRITT = 60;

/**
 * Dauer eines Huellen-Auftritts, kuerzer als die 340 ms des Seiten-Aufbaus.
 *
 * Die Huelle hat sechs bis acht Zeichen, die NACHEINANDER gelesen werden
 * sollen; eine Liste hat Dutzende, die als Wirbel gemeint sind. Dieselbe Dauer
 * fuer beides hiesse, eines von beidem falsch zu machen.
 *
 * Wird als Inline-Stil gesetzt und ueberschreibt damit `animation-duration`
 * aus `.jw-aufbau` — bewusst kein zweiter Klassenname in `globals.css`: die
 * Kurve, die Fuellung und der `calc()`-Verzoegerungsterm sollen dieselben
 * bleiben, nur die Dauer ist anders.
 */
export const HUELLE_DAUER_MS = 200;

/**
 * Gesamtfenster fuer die Eintraege einer Seitenleiste.
 *
 * Die Kopfzeile hat sechs bis acht Zeichen, eine Navigation kann fuenfzehn
 * Zeilen haben (automations: vier Abschnitte plus Ueberschriften). Mit festen
 * 60 ms je Zeile stuende die letzte bei 900 ms — die Kopfzeile ist da lange
 * fertig, und der Blick wartet unten auf einen Nachzuegler.
 *
 * Deshalb ist der Abstand hier nicht fest, sondern das FENSTER: bei wenigen
 * Zeilen bleibt es bei `HUELLE_SCHRITT`, bei vielen ruecken sie zusammen. Die
 * letzte Zeile steht damit nie spaeter als `HUELLE_LEISTE + 300` = 420 ms —
 * gut eine Zehntelsekunde nach dem letzten Schlag der Kopfzeile (300 ms), also
 * noch als dieselbe Bewegung lesbar.
 *
 * Dasselbe Muster wie `stufenVerzug` in ai-chats `eintritts-staffel.ts`.
 */
export const HUELLE_LEISTE_FENSTER = 300;

/**
 * Zeitpunkt fuer die `index`-te von `anzahl` Zeilen einer Seitenleiste.
 *
 * @example
 *   <a className={AUFBAU_KLASSE} style={leistenStil(3, zeilen)} />
 */
export function leistenStil(index: number, anzahl: number): CSSProperties {
  const gesamt = Math.min((Math.max(anzahl, 2) - 1) * HUELLE_SCHRITT, HUELLE_LEISTE_FENSTER);
  const ab = HUELLE_LEISTE + Math.round((index / (Math.max(anzahl, 2) - 1)) * gesamt);
  return {
    "--jw-ab": `${ab}ms`,
    animationDuration: `${HUELLE_DAUER_MS}ms`,
  } as CSSProperties;
}

/**
 * Zeitpunkt fuer ein Element der Huelle.
 *
 * @example
 *   <button className={AUFBAU_KLASSE} style={huellenStil(HUELLE_KNOEPFE, 2)} />
 */
export function huellenStil(ab: number, index = 0): CSSProperties {
  return {
    "--jw-ab": `${ab + index * HUELLE_SCHRITT}ms`,
    animationDuration: `${HUELLE_DAUER_MS}ms`,
  } as CSSProperties;
}
